import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { paymentAPI } from '../utils/api';
import './SubscriptionManager.css';

interface TierConfig {
  name: string;
  displayName: string;
  monthlyCredits: number;
  storageLimit: number;
  price: number;
  features: string[];
}

interface SubscriptionManagerProps {
  tiers: Record<string, TierConfig>;
}

interface UpgradeConfirmation {
  tier: string;
  currentTierConfig: TierConfig;
  newTierConfig: TierConfig;
  proratedAmount: number;
  daysRemaining: number;
  periodEnd: Date | null;
}

const SubscriptionManager: React.FC<SubscriptionManagerProps> = ({ tiers }) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [upgradeConfirmation, setUpgradeConfirmation] = useState<UpgradeConfirmation | null>(null);

  const currentTier = user?.tier || 'free';
  const tierOrder = ['free', 'starter', 'pro', 'entrepreneur'];
  const currentTierIndex = tierOrder.indexOf(currentTier);

  // Convert array to object if needed
  const tiersObject: Record<string, TierConfig> = Array.isArray(tiers)
    ? tiers.reduce((acc, tier) => {
        acc[tier.name] = tier;
        return acc;
      }, {} as Record<string, TierConfig>)
    : tiers;

  console.log('SubscriptionManager - Current tier:', currentTier, 'Index:', currentTierIndex);
  console.log('SubscriptionManager - Tiers object:', tiersObject);

  // Check if tiers is loaded
  if (!tiersObject || Object.keys(tiersObject).length === 0) {
    return <div>Loading subscription plans...</div>;
  }

  const formatStorage = (bytes: number): string => {
    if (bytes === 0) return 'Unlimited';
    const gb = bytes / (1024 * 1024 * 1024);
    const mb = bytes / (1024 * 1024);
    
    // If less than 1 GB, show in MB
    if (gb < 1) {
      return `${Math.round(mb)} MB`;
    }
    
    return `${gb} GB`;
  };

  const handleUpgrade = async (tier: string) => {
    if (!user) return;

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      console.log('Upgrade attempt:', {
        targetTier: tier,
        currentTier,
        hasStripeSubscriptionId: !!user.stripeSubscriptionId,
        stripeSubscriptionId: user.stripeSubscriptionId
      });

      // If user already has a subscription, change the tier
      if (user.stripeSubscriptionId && currentTier !== 'free') {
        const tierIndex = tierOrder.indexOf(tier);
        const isUpgrade = tierIndex > currentTierIndex;
        
        // Check if subscription is cancelled
        if (user.subscriptionStatus === 'canceled') {
          setError('Cannot upgrade a cancelled subscription. Please wait until your current subscription ends, then subscribe to a new plan.');
          setLoading(false);
          return;
        }
        
        // Only allow upgrades
        if (!isUpgrade) {
          setError('Downgrades are not supported. Please cancel your current subscription to switch to a lower tier.');
          setLoading(false);
          return;
        }
        
        console.log('Using changeSubscription API (existing subscription)');
        
        // Calculate prorated amount
        const currentTierConfig = tiersObject[currentTier];
        const newTierConfig = tiersObject[tier];
        const priceDifference = newTierConfig.price - currentTierConfig.price;
        
        // Calculate days remaining in billing period
        const now = new Date();
        const periodEnd = user.subscriptionCurrentPeriodEnd ? new Date(user.subscriptionCurrentPeriodEnd) : null;
        let daysRemaining = 0;
        let totalDays = 30;
        let proratedAmount = priceDifference;
        
        if (periodEnd) {
          const timeRemaining = periodEnd.getTime() - now.getTime();
          daysRemaining = Math.ceil(timeRemaining / (1000 * 60 * 60 * 24));
          
          // Estimate total days in period (assume monthly)
          totalDays = 30;
          
          // Calculate prorated amount
          proratedAmount = (priceDifference * daysRemaining) / totalDays;
        }
        
        // Show upgrade confirmation modal
        setUpgradeConfirmation({
          tier,
          currentTierConfig,
          newTierConfig,
          proratedAmount,
          daysRemaining,
          periodEnd
        });
        setLoading(false);
      } else {
        // New subscription - redirect to Stripe Checkout
        console.log('Using createSubscription API (new subscription)');
        const response = await paymentAPI.createSubscription(tier);
        if (response.success && response.url) {
          window.location.href = response.url;
        }
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to update subscription');
      setLoading(false);
    }
  };

  const confirmUpgrade = async () => {
    if (!upgradeConfirmation) return;

    setLoading(true);
    setError(null);
    setSuccess(null);
    setUpgradeConfirmation(null);

    try {
      const response = await paymentAPI.changeSubscription(upgradeConfirmation.tier);
      if (response.success) {
        setSuccess('Subscription upgraded successfully!');
        
        // Refresh user data after a short delay
        setTimeout(() => {
          window.location.reload();
        }, 2000);
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to update subscription');
    } finally {
      setLoading(false);
    }
  };

  const cancelUpgrade = () => {
    setUpgradeConfirmation(null);
  };

  const handleCancelSubscription = async () => {
    if (!user) return;

    if (!window.confirm('Are you sure you want to cancel your subscription? You will retain access until the end of your billing period.')) {
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await paymentAPI.cancelSubscription();
      if (response.success) {
        setSuccess(response.message || 'Subscription cancelled successfully');
        // Refresh user data
        setTimeout(() => {
          window.location.reload();
        }, 2000);
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to cancel subscription');
    } finally {
      setLoading(false);
    }
  };

  const handleManageSubscription = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await paymentAPI.getPortalUrl();
      if (response.success && response.url) {
        // Redirect to Stripe Customer Portal
        window.location.href = response.url;
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to open subscription portal');
      setLoading(false);
    }
  };

  return (
    <div className="subscription-manager">
      <h2>Subscription Plans</h2>

      {error && <div className="alert alert-error">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      {/* Upgrade Confirmation Modal */}
      {upgradeConfirmation && (
        <div className="modal-overlay" onClick={cancelUpgrade}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Subscription Upgrade Confirmation</h3>
              <button className="modal-close" onClick={cancelUpgrade}>&times;</button>
            </div>
            <div className="modal-body">
              <div className="upgrade-comparison">
                <div className="upgrade-section">
                  <h4>Current Plan</h4>
                  <p className="plan-name">{upgradeConfirmation.currentTierConfig.displayName}</p>
                  <p className="plan-price">${upgradeConfirmation.currentTierConfig.price}/month</p>
                  <p className="plan-tokens">{(user?.monthlyCredits || 0).toLocaleString()} tokens/month</p>
                </div>
                <div className="upgrade-arrow">→</div>
                <div className="upgrade-section">
                  <h4>New Plan</h4>
                  <p className="plan-name">{upgradeConfirmation.newTierConfig.displayName}</p>
                  <p className="plan-price">${upgradeConfirmation.newTierConfig.price}/month</p>
                  <p className="plan-tokens">{upgradeConfirmation.newTierConfig.monthlyCredits.toLocaleString()} tokens/month</p>
                </div>
              </div>
              
              <div className="billing-details">
                <h4>Billing Details</h4>
                <div className="billing-row">
                  <span>Charge Today:</span>
                  <strong>${upgradeConfirmation.proratedAmount.toFixed(2)}</strong>
                </div>
                <div className="billing-note">
                  (Prorated for {upgradeConfirmation.daysRemaining} days remaining)
                </div>
                <div className="billing-row">
                  <span>Updated Monthly Tokens:</span>
                  <strong>{upgradeConfirmation.newTierConfig.monthlyCredits.toLocaleString()} tokens</strong>
                </div>
                <div className="billing-row">
                  <span>Next Billing Date:</span>
                  <strong>{upgradeConfirmation.periodEnd ? upgradeConfirmation.periodEnd.toLocaleDateString() : 'Unknown'}</strong>
                </div>
                <div className="billing-row">
                  <span>Next Billing Amount:</span>
                  <strong>${upgradeConfirmation.newTierConfig.price.toFixed(2)}</strong>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={cancelUpgrade} disabled={loading}>
                Cancel
              </button>
              <button className="btn btn-primary" onClick={confirmUpgrade} disabled={loading}>
                {loading ? 'Processing...' : 'Confirm Upgrade'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Current Subscription Info */}
      {currentTier !== 'free' && (
        <div className="current-subscription">
          <h3>Current Plan: {tiers[currentTier]?.displayName}</h3>
          
          {user?.subscriptionStatus === 'canceled' && user?.subscriptionCurrentPeriodEnd && (
            <div className="alert alert-warning" style={{ marginBottom: '15px' }}>
              ⚠️ Your subscription has been cancelled and will end on{' '}
              <strong>{new Date(user.subscriptionCurrentPeriodEnd).toLocaleDateString()}</strong>.
              You will retain access to your current plan benefits until then.
            </div>
          )}
          
          <div className="subscription-details">
            <p>
              <strong>Status:</strong>{' '}
              <span className={user?.subscriptionStatus === 'canceled' ? 'status-canceled' : 'status-active'}>
                {user?.subscriptionStatus === 'canceled' ? 'Cancelled (Active until period end)' : (user?.subscriptionStatus || 'Active')}
              </span>
            </p>
            {user?.subscriptionCurrentPeriodEnd && (
              <p>
                <strong>{user?.subscriptionStatus === 'canceled' ? 'Access ends on:' : 'Next billing date:'}</strong>{' '}
                {new Date(user.subscriptionCurrentPeriodEnd).toLocaleDateString()}
              </p>
            )}
            <p><strong>Monthly tokens:</strong> {user?.monthlyCredits?.toLocaleString()} tokens</p>
            <p><strong>Paid tokens:</strong> {user?.paidTokens?.toLocaleString()} tokens</p>
            <p><strong>Storage:</strong> {formatStorage(user?.storageLimit || 0)}</p>
          </div>
          <div className="subscription-actions">
            {user?.subscriptionStatus !== 'canceled' ? (
              <>
                <button 
                  className="btn btn-secondary"
                  onClick={handleManageSubscription}
                  disabled={loading}
                >
                  Manage Subscription
                </button>
                <button 
                  className="btn btn-danger"
                  onClick={handleCancelSubscription}
                  disabled={loading}
                >
                  Cancel Subscription
                </button>
              </>
            ) : (
              <>
                <button className="btn btn-secondary" disabled>
                  Manage Subscription
                </button>
                <button className="btn btn-danger" disabled>
                  Already Cancelled
                </button>
              </>
            )}
          </div>
          {user?.subscriptionStatus === 'canceled' && (
            <p style={{ fontStyle: 'italic', color: '#666', marginTop: '15px' }}>
              Your subscription is set to cancel. You can subscribe to a new plan after{' '}
              {user?.subscriptionCurrentPeriodEnd ? new Date(user.subscriptionCurrentPeriodEnd).toLocaleDateString() : 'the current period ends'}.
            </p>
          )}
        </div>
      )}

      {/* Tier Selection */}
      <div className="tier-grid">
        {tierOrder.map((tierKey) => {
          const tierConfig = tiersObject[tierKey];
          if (!tierConfig) return null;
          
          const tierIndex = tierOrder.indexOf(tierKey);
          const isCurrentTier = tierKey === currentTier;
          const isDowngrade = tierIndex < currentTierIndex;
          const isFree = tierKey === 'free';
          
          console.log(`Tier: ${tierKey}, Index: ${tierIndex}, CurrentIndex: ${currentTierIndex}, isDowngrade: ${isDowngrade}`);

          return (
            <div
              key={tierKey}
              className={`tier-card ${isCurrentTier ? 'current' : ''} ${tierKey}`}
            >
              {isCurrentTier && <div className="current-badge">Current Plan</div>}
              
              <h3>{tierConfig.displayName}</h3>
              <div className="tier-price">
                {isFree ? (
                  <span className="price">Free</span>
                ) : (
                  <>
                    <span className="price">${tierConfig.price}</span>
                    <span className="period">/month</span>
                  </>
                )}
              </div>

              <ul className="tier-features">
                {tierConfig.features.map((feature, idx) => (
                  <li key={idx}>{feature}</li>
                ))}
                <li>{tierConfig.monthlyCredits.toLocaleString()} monthly tokens</li>
                <li>{formatStorage(tierConfig.storageLimit)} storage</li>
              </ul>

              {!isFree && !isCurrentTier && !isDowngrade && (
                <button
                  className="btn btn-primary"
                  onClick={() => handleUpgrade(tierKey)}
                  disabled={loading || user?.subscriptionStatus === 'canceled'}
                >
                  {loading ? 'Processing...' : (user?.subscriptionStatus === 'canceled' ? 'Subscription Cancelled' : 'Upgrade')}
                </button>
              )}

              {!isFree && !isCurrentTier && isDowngrade && (
                <button className="btn btn-outline" disabled>
                  Cancel to Downgrade
                </button>
              )}

              {isCurrentTier && !isFree && user?.subscriptionStatus === 'canceled' && (
                <button className="btn btn-outline" disabled>
                  Cancelled (Active until {user?.subscriptionCurrentPeriodEnd ? new Date(user.subscriptionCurrentPeriodEnd).toLocaleDateString() : 'period end'})
                </button>
              )}

              {isCurrentTier && !isFree && user?.subscriptionStatus !== 'canceled' && (
                <button className="btn btn-outline" disabled>
                  Current Plan
                </button>
              )}

              {isFree && isCurrentTier && (
                <button className="btn btn-outline" disabled>
                  Current Plan
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default SubscriptionManager;
